import { ChannelType } from 'components/CampaignCreationModal/Types'
import dayjs from 'dayjs'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { AssetType } from 'hooks/useAlmanacFileSubscription'
import { transformSchedule } from 'screens/BrandKits/components/BrandPlanTimeline/useTransformedBrandPlanSchedules'
import {
  AlmanacFile,
  BrandPlanSchedule,
  BrandPlanScheduleContent,
  BrandPlanScheduleContentStatus,
  SuggestionRequestStatus,
  ViewType,
  Workspace,
} from 'types'

export const getFormattedDate = (dateString: string): string => {
  const inputDate = dayjs(dateString)
  const today = dayjs()
  const tomorrow = dayjs().add(1, 'day')
  const yesterday = dayjs().subtract(1, 'day')

  let formattedDate = inputDate.format('ddd, MMM D, h:mma')

  if (inputDate.isSame(today, 'day')) {
    formattedDate = `Today, ${inputDate.format('h:mma')}`
  } else if (inputDate.isSame(tomorrow, 'day')) {
    formattedDate = `Tomorrow, ${inputDate.format('h:mma')}`
  } else if (inputDate.isSame(yesterday, 'day')) {
    formattedDate = `Yesterday, ${inputDate.format('h:mma')}`
  }

  return formattedDate
}

// mock the rest of the fields for the almanac file, which we don't actually need or use
export const getAlmanacFiles = (eventsData: AlmanacFileCalendarItem[], workspace: Workspace) => {
  return eventsData.map((event) => {
    return {
      ...event,
      id: event.key,
      type: event.asset_type,
      post_at: event.post_at,
      workspace_id: workspace.key,
      real_workspace_id: workspace.id,
      updated_at: event.post_at,
      ancestor_projects: [],
      accessor_info: '',
      accessor_names: [],
      owner: '',
      version_control_mode: {
        value: false,
        metadata: null,
      },
      view_metadata: {
        view: ViewType.List,
      },
      is_root: false,
    }
  }) as AlmanacFile[]
}

export const docHasPreview = (content: BrandPlanScheduleContent) => {
  return content.almanac_file?.asset_type === AssetType.Article && !!content.almanac_file?.image_url
}

export const designHasPreview = (content: BrandPlanScheduleContent) => {
  return (
    content.almanac_file?.asset_type === AssetType.Design &&
    (!!content.almanac_file?.image_url || !!content.almanac_file?.design_content)
  )
}

export const contentHasPreview = (content: BrandPlanScheduleContent) => {
  return docHasPreview(content) || designHasPreview(content)
}

export const isPending = (status: BrandPlanScheduleContentStatus) => {
  const pendingStatuses: BrandPlanScheduleContentStatus[] = [
    SuggestionRequestStatus.Pending,
    SuggestionRequestStatus.Processing,
    SuggestionRequestStatus.Started,
  ]
  return pendingStatuses.includes(status)
}

export const isSuccessful = (content: BrandPlanScheduleContent) => contentHasPreview(content)

export const hasFailed = (content: BrandPlanScheduleContent) => {
  const failedStatuses: BrandPlanScheduleContentStatus[] = [
    SuggestionRequestStatus.Failed,
    SuggestionRequestStatus.Expired,
    SuggestionRequestStatus.Aborted,
  ]
  return failedStatuses.includes(content.status) || !!content.almanac_file?.discarded
}

export const hasCompleted = (content: BrandPlanScheduleContent) => hasFailed(content) || contentHasPreview(content)

// Determines if a content is in the process of being generated. A content is considered to be generating if its status
// is pending or processing, or if it is successful but lacks an image URL while the image URL is still being generated.
export const isGeneratingContent = (content: BrandPlanScheduleContent) => {
  return isContentFromLatestBatch(content) && !hasFailed(content) && !contentHasPreview(content)
}

export const isFileFromLatestBatch = (file: AlmanacFileCalendarItem) => {
  if (isArticle({ almanac_file: file })) {
    return dayjs(file.created_at).isAfter(dayjs().subtract(5, 'minutes'))
  }

  return dayjs(file.created_at).isAfter(dayjs().subtract(10, 'minutes'))
}

export const isContentFromLatestBatch = (content: BrandPlanScheduleContent) => {
  const createdAt = content.almanac_file?.created_at || content.created_at

  return dayjs(createdAt).isAfter(dayjs().subtract(5, 'minutes'))
}

// Checks if all contents in a given schedule have a preview available. A content is considered to have
// a preview if it has failed (it will be ignored), or if it has an image URL or design content available.
export const isGenerationDone = (schedule: BrandPlanSchedule) => {
  return schedule?.contents
    .filter((content) => isContentFromLatestBatch(content))
    .every((content) => hasFailed(content) || contentHasPreview(content))
}

// Finds a schedule that is currently generating content. A schedule is considered generating if it has
// contents and either the schedule status is pending or any of its contents are generating.
export const getGeneratingSchedule = (schedules: BrandPlanSchedule[]) => {
  return schedules?.find(
    (schedule) =>
      schedule.contents.length > 0 &&
      (isPending(schedule.status) || schedule.contents.some((content) => isGeneratingContent(content))),
  )
}

export const getGeneratingSchedules = (schedules: BrandPlanSchedule[]) => {
  return schedules
    .filter((s) => isPending(s.status) || (s.contents.length > 0 && s.contents.some(isGeneratingContent)))
    .map(transformSchedule)
}

export const isArticle = ({
  almanac_file,
  content,
}: {
  // Widened to the union so callers can pass either the calendar shape
  // (still used by calendar/event consumers) or the show-item shape
  // (now backing `BrandPlanScheduleContent.almanac_file`). Both expose
  // `asset_type` and `channel`, which is all this function inspects.
  almanac_file?: AlmanacFileCalendarItem | AlmanacFileShowItem
  content?: BrandPlanScheduleContent
}) => {
  if (content) {
    return (
      [ChannelType.BlogPost, ChannelType.Newsletter].includes(content.type as ChannelType) ||
      isFileArticle(content.almanac_file)
    )
  }

  if (almanac_file) return isFileArticle(almanac_file)

  return undefined
}

export const hasContentInLatestBatchCreatedWithinLastSeconds = (
  contents: BrandPlanScheduleContent[],
  seconds: number,
) =>
  contents.some((content) => {
    const createdAt = content.almanac_file?.created_at || content.created_at

    return createdAt != null && dayjs(createdAt).isAfter(dayjs().subtract(seconds, 'seconds'))
  })

const isFileArticle = (almanac_file?: AlmanacFileCalendarItem | AlmanacFileShowItem) => {
  return (
    almanac_file?.asset_type === AssetType.Article ||
    (almanac_file?.channel &&
      [ChannelType.BlogPost, ChannelType.Newsletter].includes(almanac_file.channel as ChannelType))
  )
}
